import React, {useEffect, useState} from 'react';

import {IMG_DIR} from '#Client/components/constants';
import {DateCalendar} from '#Client/components/DateCalendar/DateCalendar';
import {
  leaderboardsHeading,
  leaderboardsInstruction,
  leaderboardsMedal,
  leaderboardsRow,
  leaderboardsRowMe,
  leaderboardsStar,
  leaderboardsTable,
} from '#Client/components/LeaderboardsModal/LeaderboardsModal.css';
import {Modal} from '#Client/components/Modal/Modal';
import {Spinner} from '#Client/components/Spinner/Spinner';
import {StarGroup} from '#Client/components/StarGroup/StarGroup';
import {Tabs} from '#Client/components/Tabs/Tabs';
import {
  getLeaderboards,
  LeaderboardResponse,
  LeaderboardResult,
} from '#Common/api/api';
import {getNumberWithOrdinalSuffix} from '#Common/util';

type LeaderboardsModalProps = {
  onClose: () => void;
};

const getTable = (data: LeaderboardResult[]) => {
  return (
    <table className={leaderboardsTable}>
      <thead>
        <tr className={leaderboardsRow}>
          <th className="textAlignLeft">Rank</th>
          <th className="textAlignCenter">Player Name</th>
          <th className="textAlignCenter">
            <img
              className={leaderboardsStar}
              height="64"
              src={`${IMG_DIR}icon_star.png`}
              width="64"
            />
          </th>
        </tr>
      </thead>
      <tbody>
        {data.map((result: LeaderboardResult, index: number) => (
          <tr
            className={`${leaderboardsRow} ${
              result.isMe ? leaderboardsRowMe : ''
            }`}
            key={index}
          >
            <td className="textAlignLeft">
              {result.rank < 4 ? (
                <img
                  className={leaderboardsMedal}
                  height="90"
                  src={`${IMG_DIR}ranking_medal_${result.rank}.png`}
                  width="90"
                />
              ) : (
                getNumberWithOrdinalSuffix(result.rank)
              )}
            </td>
            <td className="textAlignCenter">
              {result.name || 'Name not entered'}
              {result.isMe ? ' (you)' : ''}
            </td>
            <td className="textAlignCenter">{result.score}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export const LeaderboardsModal = ({onClose}: LeaderboardsModalProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [leaderboardData, setLeaderboardData] = useState<LeaderboardResponse>();
  useEffect(() => {
    (async () => {
      const leaderboards = await getLeaderboards();
      setLeaderboardData(leaderboards);
      setIsLoading(false);
    })();
  }, []);
  return (
    <Modal
      image={`${IMG_DIR}icon_rankings.png`}
      title="Leaderboards"
      onClose={onClose}
    >
      {isLoading || typeof leaderboardData === 'undefined' ? (
        <div className="textAlignCenter">
          Loading leaderboards. Just a sec. <Spinner />
        </div>
      ) : (
        <div>
          <Tabs
            tabs={[
              {
                title: 'Today',
                content: (
                  <div>
                    <h3 className={leaderboardsHeading}>
                      <DateCalendar
                        date={leaderboardData.dateString}
                        size="small"
                      />
                    </h3>
                    {getTable(leaderboardData.todayResults)}
                  </div>
                ),
              },
              {
                title: 'This Week',
                content: (
                  <div>
                    <h3 className={leaderboardsHeading}>
                      <DateCalendar
                        date={leaderboardData.firstDayOfWeekString}
                        size="small"
                      />{' '}
                      through{' '}
                      <DateCalendar
                        date={leaderboardData.lastDayOfWeekString}
                        size="small"
                      />
                    </h3>
                    {getTable(leaderboardData.thisWeekResults)}
                  </div>
                ),
              },
              {
                title: 'Last Week',
                content: (
                  <div>
                    <h3 className={leaderboardsHeading}>
                      <DateCalendar
                        date={leaderboardData.firstDayOfLastWeekString}
                        size="small"
                      />{' '}
                      through{' '}
                      <DateCalendar
                        date={leaderboardData.lastDayOfLastWeekString}
                        size="small"
                      />
                    </h3>
                    {getTable(leaderboardData.lastWeekResults)}
                  </div>
                ),
              },
              {
                title: 'Help',
                content: (
                  <div>
                    <h3 className={leaderboardsHeading}>How to get stars</h3>
                    <div>
                      Get stars by solving puzzles. Earn up to 10 stars each
                      day!
                      <div className={leaderboardsInstruction}>
                        <div>
                          Solve <b>Easy</b> to get 1 star.
                        </div>
                        <StarGroup empty={0} filled={1} />
                      </div>
                      <div className={leaderboardsInstruction}>
                        <div>
                          Solve <b>Medium</b> to get 2 stars.
                        </div>
                        <StarGroup empty={0} filled={2} />
                      </div>
                      <div className={leaderboardsInstruction}>
                        <div>
                          Solve <b>Hard</b> to get 3 stars.
                        </div>
                        <StarGroup empty={0} filled={3} />
                      </div>
                      <div className={leaderboardsInstruction}>
                        <div>
                          Solve <b>Brutal</b> to get 4 stars.
                        </div>
                        <StarGroup empty={0} filled={4} />
                      </div>
                    </div>
                    <div className={leaderboardsInstruction}>
                      <b>Optional:</b> Enter your name in &apos;Settings&apos;
                      for display on the Leaderboard
                    </div>
                  </div>
                ),
              },
            ]}
          />
        </div>
      )}
    </Modal>
  );
};
